/**
 * A notificação curta no rodapé dos miolos.
 *
 * Papel: desenha o `#toast` das duas origens — rodapé central, cápsula escura ou
 * vermelha, 3 s visível, transição de opacidade de 0,3 s — e fornece o
 * `notificar` aos componentes de dentro.
 *
 * Como usar: quem monta é o `QuadroEmbutido`; nenhum módulo precisa montar
 * outro. Uma mensagem nova substitui a que está à vista e reinicia os 3 s, como
 * o `clearTimeout` da origem. O elemento fica sempre no DOM, com opacidade zero
 * quando escondido, e por isso é `pointer-events:none`: sem isso, a cápsula
 * invisível engoliria o clique no que estivesse atrás dela.
 */
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/shared/lib/utils';
import { ContextoDeNotificacao, type Notificar } from './contexto-de-notificacao';

/** Quanto tempo a mensagem fica à vista, o mesmo da origem. */
const DURACAO_EM_MS = 3000;

interface EstadoDaNotificacao {
  mensagem: string;
  ok: boolean;
  visivel: boolean;
}

/**
 * Fornece a notificação curta e a desenha.
 *
 * @param children - O conteúdo do miolo.
 * @returns O conteúdo e a cápsula da notificação.
 */
export function ProvedorDeNotificacao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoDaNotificacao>({
    mensagem: '',
    ok: true,
    visivel: false,
  });
  const temporizador = useRef<number | null>(null);

  const notificar = useCallback<Notificar>((mensagem, ok = true) => {
    setEstado({ mensagem, ok, visivel: true });
    if (temporizador.current !== null) window.clearTimeout(temporizador.current);
    temporizador.current = window.setTimeout(() => {
      setEstado((anterior) => ({ ...anterior, visivel: false }));
    }, DURACAO_EM_MS);
  }, []);

  useEffect(
    () => () => {
      if (temporizador.current !== null) window.clearTimeout(temporizador.current);
    },
    [],
  );

  return (
    <ContextoDeNotificacao value={notificar}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'pointer-events-none fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2 rounded-[10px] px-[22px] py-2.5 font-sans text-[13px] font-semibold whitespace-nowrap text-superficie transition-opacity duration-300 print:hidden',
          estado.ok ? 'bg-emb-notificacao' : 'bg-emb-notificacao-erro',
          estado.visivel ? 'opacity-100' : 'opacity-0',
        )}
      >
        {estado.mensagem}
      </div>
    </ContextoDeNotificacao>
  );
}
